<template>
	<div>
		<SliderLayout :min-height="minHeight()">
			<div>
				<helio-toast
					v-bind="infoMessageProperties"
					:countdown="10"
					icon="info-circle"
					icon-weight="light"
					@close="infoMessage = null"
				/>
				<div
					class="login__centiva"
					data-cy="login-centiva"
					@click="redirectToCentiva()"
				>
					<div>
						<img
							:src="require('@/assets/Centiva_Dark.svg')"
							width="183"
							height="33"
						>
					</div>
				</div>
				<div
					class="login__error"
					data-cy="login-error"
				>
					<helio-message
						:active="isErrorPresent"
						icon-token="exclamation-circle"
						icon-weight="bold"
						state="danger"
						:title="error.title"
						@close="resetError"
					>
						<template v-slot:content>
							<p>
								{{ error.description }}
							</p>
						</template>
					</helio-message>
				</div>
				<div class="login__input">
					<helio-field
						ref="email-field"
						data-cy="email-field"
						watch-auto-fill
						:display-success="false"
						type="email"
						autofocus
						icon="user-circle"
						icon-weight="light"
						:initial-value="email"
						:label="translation['email']"
						large="large"
						@keyup.enter.native="!loginDisabled ? login() : () => {}"
						@errors="emailErrors = $event"
						@changed="email = $event"
					/>
					<helio-field
						ref="password-field"
						data-cy="password-field"
						watch-auto-fill
						:display-success="false"
						:label="translation['password']"
						icon="key"
						icon-weight="light"
						large="large"
						type="password"
						@changed="password = $event"
						@keyup.enter.native="!loginDisabled ? login() : () => {}"
					/>
					<helio-button
						:disabled="loginDisabled"
						class="login__button"
						:state="buttonState"
						color="primary"
						:text="translation['login']"
						size="large"
						@click="login"
					/>
					<router-link
						class="login__forgot-password"
						:to="{ name: `forgot-password-${$i18n.locale}` }"
						data-cy="forgot-password"
					>
						{{ translation["forgot-password"] }}
					</router-link>
				</div>
			</div>
		</SliderLayout>
		<LoginAnimation
			v-if="activateLoginAnimation"
			:aborted="abortAnimation"
		/>
	</div>
</template>

<script>
import { translationMixin, titleMixin } from '@/mixins';

import LoginAnimation from './LoginAnimation';
import SliderLayout from '@/views/SliderLayout';

const ERROR_CODE_USERNAME_OR_PASSWORD_REQUIRED = 422;
const ERROR_CODE_INVALID_EMAIL_OR_PASSWORD = 401;
const ERROR_CODE_TOO_MANY_LOGIN_ATTEMPTS = 429;
const ERROR_CODE_SECURE_PASSWORD = 250;
const ERROR_CODE_FORGOT_PASSWORD = 251;
const OK = 200;

const ANIMATION_DURATION = 4000;

const DATA_KEY_RESET_TOKEN = 'reset_password_token';

export default {
	name: 'Login',
	components: {
		LoginAnimation,
		SliderLayout,
	},
	mixins: [translationMixin, titleMixin],
	data() {
		return {
			activateLoginAnimation: false,
			email: '',
			password: '',
			error: {
				title: '',
				description: '',
			},
			emailErrors: [],
			infoMessage: null,
			enableLogin: true,
			currentLayout: null,
			abortAnimation: false,
		};
	},
	computed: {
		translation() {
			return this.currentTranslation.login;
		},

		forgotPasswordLink() {
			return this.currentTranslation.names['forgot-password'];
		},

		loginDisabled() {
			return (
				this.email.length <= 0 || this.password.length <= 0 || this.emailErrors.length > 0
			);
		},

		infoMessageProperties() {
			if (this.infoMessage) {
				return {
					state: this.infoMessage.success ? 'success' : 'danger',
					active: true,
					subtitle: this.infoMessage.message,
					style: 'z-index: 100',
				};
			}

			return {
				active: false,
				subtitle: '',
			};
		},

		buttonState() {
			if (this.error.title.length > 1) {
				return 'is-unsuccessful';
			}
			if (this.activateLoginAnimation) {
				return 'is-successful';
			}
			if (!this.enableLogin) {
				return 'is-loading';
			}
			return null;
		},

		isErrorPresent() {
			return this.error.description.length > 0 && this.error.title.length > 0;
		},
	},
	created() {
		const email = this.$store.getters['application/getEmail'];

		if (email != null) {
			this.email = email;
			this.$store.dispatch('application/INVALIDATE_PROPERTY', 'email');
		}

		window.addEventListener('resize', this.onResize);
		this.onResize();
	},
	destroyed() {
		window.removeEventListener('resize', this.onResize);
	},
	mounted() {
		this.checkInfoMessage();
	},
	methods: {
		minHeight() {
			return this.isErrorPresent ? 720 : 624;
		},
		onResize() {
			if (
				this.activateLoginAnimation
				&& this.currentLayout !== this.determineCurrentLayout()
			) {
				this.abortAnimation = true;
			}

			this.currentLayout = this.determineCurrentLayout();
		},
		determineCurrentLayout() {
			return document.body.clientWidth > 1080 ? 'double' : 'single';
		},
		resetError() {
			this.error = {
				title: '',
				description: '',
			};
		},
		forceUnfocusInputs() {
			const emailField = this.$refs['email-field'].$refs.input;
			const passwordField = this.$refs['password-field'].$refs.input;

			emailField.disabled = true;
			passwordField.disabled = true;

			this.$nextTick(() => {
				emailField.disabled = false;
				passwordField.disabled = false;
			});
		},
		login() {
			// We force unfocus inputs in order to fix the Edge/IE bug when
			// the cursor of the focus input goes over the transition
			this.forceUnfocusInputs();
			this.enableLogin = false;

			this.resetError();

			this.$store
				.dispatch('sso/LOGIN', {
					email: this.email,
					password: this.password,
				})
				.then((response) => {
					const { code, data } = response.data;

					if (
						code != null
						&& code === ERROR_CODE_SECURE_PASSWORD
						&& data[DATA_KEY_RESET_TOKEN]
					) {
						this.$store
							.dispatch('application/SET_RESET_TOKEN_AND_EMAIL', {
								resetToken: data[DATA_KEY_RESET_TOKEN],
								email: this.email,
							})
							.then(() => {
								this.$router.push({ name: `secure-password-${this.$i18n.locale}` });
							})
							.catch((e) => {
								this.error.title = 'Invalid token';
								this.error.description = e;
							});
					} else if (
						code != null
						&& code === ERROR_CODE_FORGOT_PASSWORD
						&& data[DATA_KEY_RESET_TOKEN]
					) {
						const lang = this.$i18n.locale;
						const endpoint = `${window.location.origin}/password/reset-code`;
						const params = `code=${data[DATA_KEY_RESET_TOKEN]}&email=${this.email}&lang=${lang}`;
						window.location = `${endpoint}?${params}`;
					} else if (code === OK) {
						this.$store.dispatch('sso/SAVE_TOKENS', response.data.data).then(() => {
							this.activateLoginAnimation = true;
							setTimeout(() => {
								let redirectDomain = this.$store.getters['application/getRedirect'];
								if (redirectDomain == null) {
									redirectDomain = `${process.env.VUE_APP_CENTIVA_PROTOCOL}://${process.env.VUE_APP_CENTIVA_DOMAIN}`;
								}
								window.location = redirectDomain;
							}, ANIMATION_DURATION);
						});
					}
				})
				.catch((error) => {
					this.enableLogin = true;
					if (error.response == null) {
						this.error.title = this.translation['title-login-failed'];
						this.error.description = this.translation['login-failed'];
					} else {
						const { code } = error.response.data;
						switch (code) {
							case ERROR_CODE_USERNAME_OR_PASSWORD_REQUIRED:
								this.error.title = this.translation[
									'title-username-password-required'
								];
								this.error.description = this.translation[
									'username-password-required'
								];
								break;
							case ERROR_CODE_INVALID_EMAIL_OR_PASSWORD:
								this.error.title = this.translation[
									'title-invalid-email-or-password'
								];
								this.error.description = this.translation[
									'invalid-email-or-password'
								];
								break;
							case ERROR_CODE_TOO_MANY_LOGIN_ATTEMPTS:
								this.error.title = this.translation['title-too-many-login-attempt'];
								this.error.description = this.translation['too-many-login-attempt'];
								break;
							default:
								this.error.title = this.translation['title-login-failed'];
								this.error.description = this.translation['login-failed'];
						}
					}
				});
		},
		checkInfoMessage() {
			const info = this.$store.getters['application/getInfoMessage'];
			if (info != null) {
				this.infoMessage = info;
				this.$store.dispatch('application/INVALIDATE_PROPERTY', 'infoMessage');
			}
		},
		redirectToCentiva() {
			if (this.$i18n.locale === 'en') {
				window.open('https://centiva.io/en', '_blank');
			} else {
				window.open('https://centiva.io/', '_blank');
			}
		},
	},
};
</script>

<style lang="scss" src="./login.scss" />
